import { Static, Type } from '@sinclair/typebox';

/**
 * ServiceArea — zone d'intervention d'un pro (ville, département ou région).
 *
 * Renommé depuis `ServiceCity` pour refléter qu'on accepte aussi les
 * départements et régions (via Google Places `administrative_area_level_*`).
 * Shape interne **provider-agnostique** : on ne persiste aucun identifiant
 * Google (place_id). Demain on peut swap pour BAN, Mapbox, OSM ou INSEE
 * sans migration de données. Le matching cross-pros se fait via un `area_key`
 * dérivé déterministiquement au write (cf. {@link buildAreaKey}).
 *
 * Voir docs/to_implement/website-locations-pages-plan.md.
 */
export enum ServiceAreaType {
  CITY = 'CITY',
  DEPARTMENT = 'DEPARTMENT',
  REGION = 'REGION',
}

export const ServiceAreaTypeSchema = Type.Enum(ServiceAreaType);

export const ServiceAreaSchema = Type.Object({
  /**
   * Clé dérivée stable, calculée côté service au moment du POST/PATCH.
   * Format : `${prefix}${slugify(name)}:${country_code.toLowerCase()}`.
   * - city       → `lyon:fr`
   * - department → `dept-rhone:fr`
   * - region     → `region-ara:fr`
   *
   * Le préfixe par type évite les collisions entre homonymes inter-types
   * (ex. "Lyon" ville vs "Rhône" département). Utilisée pour le matching
   * cross-pros via index GIN sur `professional.service_areas` (annuaire futur).
   * Ne provient JAMAIS du provider externe → reste valable après un swap.
   */
  area_key: Type.String({ pattern: '^[a-z0-9-]+:[a-z]{2}$', maxLength: 160 }),

  /** Type de zone — pilote le préfixe `area_key` et le JSON-LD `areaServed`. */
  area_type: ServiceAreaTypeSchema,

  /**
   * Slug URL dérivé du `name` au write, stocké pour usage public stable.
   * Format `[a-z0-9-]+`, max 120 chars. Détection de collision côté write :
   * en cas de doublon dans le tableau, on suffixe `-2`, `-3`, etc.
   * Sert à l'URL publique `/lieux/[url_slug]`.
   */
  url_slug: Type.String({ pattern: '^[a-z0-9-]+$', maxLength: 120 }),

  /** Nom court de la zone affiché, ex : "Lyon", "Rhône", "Île-de-France". */
  name: Type.String({ minLength: 1, maxLength: 120 }),
  /** Libellé affichable complet, ex : "Lyon, France" / "Rhône, France". */
  formatted_address: Type.String({ minLength: 1, maxLength: 300 }),
  /** ISO 3166-1 alpha-2, ex : "FR", "BE", "CH". */
  country_code: Type.String({ pattern: '^[A-Z]{2}$' }),
  /**
   * Zone administrative parente, ex : "Auvergne-Rhône-Alpes" pour une ville.
   * Optionnelle. Pour une région, peut rester vide (la zone EST la région).
   */
  admin_area: Type.Optional(Type.String({ maxLength: 120 })),
  postal_code: Type.Optional(Type.String({ maxLength: 20 })),

  /**
   * Coordonnées optionnelles : utiles pour villes/départements (pin sur
   * carte, calcul rayon AT_HOME). Pour les régions, sont la coordonnée du
   * centroïde renvoyé par Google ou laissées vides.
   */
  lat: Type.Optional(Type.Number({ minimum: -90, maximum: 90 })),
  lng: Type.Optional(Type.Number({ minimum: -180, maximum: 180 })),

  /**
   * Exactement **une** entrée a `is_primary === true` dans la liste (invariant
   * applicatif rebalancé en transaction côté backend). La primary alimente
   * `{primary_area}` dans les templates SEO. Par règle UX, suit toujours
   * `display_order === 0` (cf. règle reorderServiceAreas dans le service).
   */
  is_primary: Type.Boolean(),
  display_order: Type.Integer({ minimum: 0 }),
});

export type ServiceArea = Static<typeof ServiceAreaSchema>;

/** Cap dur à 100 entries (toutes catégories confondues) : besoins SEO/GEO des pros multi-villes (VTC…). */
export const MAX_SERVICE_AREAS = 100;

export const ServiceAreasSchema = Type.Array(ServiceAreaSchema, {
  maxItems: MAX_SERVICE_AREAS,
});

export type ServiceAreas = Static<typeof ServiceAreasSchema>;

/**
 * Slugify déterministe d'un nom de zone, partagé par {@link buildAreaKey} et
 * {@link buildUrlSlug}. Strip diacritiques (NFD + range combining marks),
 * normalise casse, garde uniquement [a-z0-9-].
 */
function slugifyAreaName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritics (NFD range)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Préfixe stable selon le type de zone, utilisé dans `area_key` pour éviter
 * les collisions homonymes inter-types.
 */
function areaKeyPrefix(type: ServiceAreaType): string {
  switch (type) {
    case ServiceAreaType.CITY:
      return '';
    case ServiceAreaType.DEPARTMENT:
      return 'dept-';
    case ServiceAreaType.REGION:
      return 'region-';
  }
}

/**
 * Construit l'`area_key` stable à partir du type, du nom et du country_code.
 *
 * Exemples :
 *   buildAreaKey(CITY, 'Lyon', 'FR')                === 'lyon:fr'
 *   buildAreaKey(CITY, 'Saint-Étienne', 'FR')       === 'saint-etienne:fr'
 *   buildAreaKey(DEPARTMENT, 'Rhône', 'FR')         === 'dept-rhone:fr'
 *   buildAreaKey(REGION, 'Île-de-France', 'FR')     === 'region-ile-de-france:fr'
 */
export function buildAreaKey(type: ServiceAreaType, name: string, country_code: string): string {
  const slug = slugifyAreaName(name);
  const prefix = areaKeyPrefix(type);
  return `${prefix}${slug}:${country_code.toLowerCase()}`;
}

/**
 * Construit le slug URL public d'une zone à partir de son label.
 * Pas de préfixe par type — l'URL est `/lieux/[slug]` directement.
 * En cas de collision avec une autre zone du même pro, le caller suffixe
 * `-2`, `-3`, etc. (cf. ensureUniqueUrlSlug côté service).
 *
 * Exemples :
 *   buildUrlSlug('Lyon')          === 'lyon'
 *   buildUrlSlug('Île-de-France') === 'ile-de-france'
 *   buildUrlSlug('Rhône')         === 'rhone'
 */
export function buildUrlSlug(name: string): string {
  return slugifyAreaName(name);
}

/**
 * Renvoie l'entrée primaire (`is_primary === true`) ou `null` si la liste est
 * vide / aucune entrée n'est marquée. Ne fait **aucun fallback** sur display_order.
 */
export function pickPrimaryServiceArea(areas: readonly ServiceArea[]): ServiceArea | null {
  return areas.find((a): boolean => a.is_primary === true) ?? null;
}

/**
 * Concatène les noms de zones pour les meta descriptions et og:description.
 * Ex : "Lyon, Villeurbanne, Rhône".
 */
export function formatAreaList(areas: readonly ServiceArea[], separator = ', '): string {
  return areas
    .slice()
    .sort((a, b): number => a.display_order - b.display_order)
    .map((a): string => a.name)
    .join(separator);
}
